import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getLocalStorageItem, popup } from '../utils';
import { allManagementRole, LOCAL_STORAGE, ROLES } from '../constants';
import { connectSocket } from '../constants/socketClient';
import { useDispatch } from 'react-redux';
import {
    addMessage,
    addOnlineUser,
    incrementChatNotification,
    removeOnlineUser,
    setOnlineUsers,
} from '../redux';
import { CURRENT_VIEW } from '../components/shared/FloatingChatElement/constant';
import { getAllOnlineUsersAPI } from '../utils/apiServices/chatsAPI';
import { CRON_EVENT, SALESFORCE_CRON_STATUS } from '../constants/cron';
import { toast } from 'react-toastify';

const useSocket = () => {
    const dispatch = useDispatch();
    const role = useSelector((state) => state.user.role);
    const isChatOpen = useSelector((s) => s.chat.isChatOpen);
    const currentView = useSelector((s) => s.chat.currentView);
    const csmCaseloadData = useSelector((s) => s.csmCaseload?.csmCaseloadData);
    const isCSMCaseload = useSelector((s) => s.csmCaseload?.isCsmCaseload);
    const currentCsm = getLocalStorageItem(LOCAL_STORAGE.CURRENT_CSM);
    const userId = isCSMCaseload ? csmCaseloadData?.id : getLocalStorageItem(LOCAL_STORAGE.USER_ID);
    const isAllManagementRole = allManagementRole(role);
    const toastIdRef = useRef(null);

    const handleMessage = (payload) => {
        if (payload?.receiver === userId) {
            if (role === ROLES.USER && payload?.sender === currentCsm) {
                dispatch(addMessage(payload));
                dispatch(incrementChatNotification());
                !isChatOpen &&
                    popup('info', `you got a new message from CSM: ${payload.sender_name}`);
            } else if (role === ROLES.USER && payload?.sender !== currentCsm) {
                popup('info', `you got a new message from CSM: ${payload.sender_name}`);
            }

            if (role !== ROLES.USER && currentView !== CURRENT_VIEW.MESSAGE_VIEW) {
                dispatch(addMessage(payload));
                dispatch(incrementChatNotification());
                !isChatOpen && popup('info', `you got a new message from ${payload.sender_name}`);
            }
        }
    };

    const handleSalesforceCronStatus = (data) => {
        const dataObject = {
            render: data.message,
            type:
                data.status === SALESFORCE_CRON_STATUS.COMPLETED
                    ? 'success'
                    : data.status === SALESFORCE_CRON_STATUS.FAILED
                      ? 'error'
                      : 'info',
            isLoading: false,
            autoClose: 5000,
        };
        if (data.status === SALESFORCE_CRON_STATUS.IN_PROGRESS) {
            toastIdRef.current = toast.loading(data.message, { closeOnClick: false });
            return;
        }
        toast.update(toastIdRef.current, dataObject);
    };

    useEffect(() => {
        if (!userId) return;
        const socket = connectSocket(userId);

        // 🔐 Register the user at page refresh
        socket.on('connect', async () => {
            socket.emit('register-user', { user_id: userId });
            const res = await getAllOnlineUsersAPI();
            dispatch(setOnlineUsers(res?.data || []));
        });

        if (isAllManagementRole) {
            socket.on(CRON_EVENT.SALESFORCE_CRON, handleSalesforceCronStatus);
        }

        socket.on('receive-message', handleMessage);

        socket.on('user-online', (payload) => {
            if (userId !== payload?.user_id) {
                dispatch(addOnlineUser(payload?.user_id));
            }
        });

        socket.on('user-offline', (payload) => {
            dispatch(removeOnlineUser(payload?.user_id));
        });

        return () => {
            // Optional: not strictly required unless remounting
            socket.off('receive-message');
            socket.off('user-online');
            socket.off('user-offline');
            socket.off(CRON_EVENT.SALESFORCE_CRON);
            socket.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, isChatOpen, role, isCSMCaseload, isAllManagementRole]);
};

export default useSocket;
